import { Fragment, type KeyboardEvent, type MouseEvent } from 'react';
import { Menu } from '@base-ui/react';
import { Icon } from '@components/icon/Icon';
import cx from 'classnames';
import typography from '~styles/typography.scss';
import {
  DATE_FILTER_GROUPS,
  DateFilterOption,
  getDateFilterLabel,
} from './dateRange';
import styles from './FinancesFilters.scss';

export interface FinancesFiltersProps {
  dateFilter: DateFilterOption;
  customStartDate: string;
  customEndDate: string;
  compareEnabled: boolean;
  onDateFilterChange: (filter: DateFilterOption) => void;
  onCustomStartDateChange: (value: string) => void;
  onCustomEndDateChange: (value: string) => void;
  onCompareToggle: (enabled: boolean) => void;
}

const openDatePicker = (input: HTMLInputElement) => {
  if (typeof input.showPicker !== 'function') {
    return;
  }
  try {
    input.showPicker();
  } catch {
    // Some browsers throw if a picker is already open or unsupported; ignore.
  }
};

// Prevent the native focus/segment selection (the blue highlight) on click and
// open the calendar picker instead.
const handleDateInputMouseDown = (event: MouseEvent<HTMLInputElement>) => {
  event.preventDefault();
  openDatePicker(event.currentTarget);
};

// Block manual typing so the value can only be set via the calendar picker.
const handleDateInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
  if (event.key === 'Tab') {
    return;
  }
  event.preventDefault();
  if (event.key === 'Enter' || event.key === ' ') {
    openDatePicker(event.currentTarget);
  }
};

export const FinancesFilters = ({
  dateFilter,
  customStartDate,
  customEndDate,
  compareEnabled,
  onDateFilterChange,
  onCustomStartDateChange,
  onCustomEndDateChange,
  onCompareToggle,
}: FinancesFiltersProps) => (
  <div className={styles.filters}>
    <Menu.Root>
      <Menu.Trigger className={styles.dateTrigger}>
        <Icon name="calendar" className={styles.dateTriggerIcon} />
        <span className={cx(typography.c3, styles.dateTriggerLabel)}>Date</span>
        <span className={cx(typography.c3, styles.dateTriggerValue)}>
          {getDateFilterLabel(dateFilter)}
        </span>
        <Icon name="chevron" className={styles.dateTriggerChevron} />
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Positioner
          align="start"
          className={styles.datePositioner}
          side="bottom"
          sideOffset={8}
        >
          <Menu.Popup className={styles.datePopup}>
            {DATE_FILTER_GROUPS.map((group, groupIndex) => (
              <Fragment key={group[0].value}>
                {groupIndex > 0 && (
                  <Menu.Separator className={styles.dateSeparator} />
                )}
                {group.map((option) => (
                  <Menu.Item
                    key={option.value}
                    className={cx(typography.t2, styles.dateOption, {
                      [styles.dateOptionSelected]: option.value === dateFilter,
                    })}
                    onClick={() => onDateFilterChange(option.value)}
                  >
                    {option.label}
                  </Menu.Item>
                ))}
              </Fragment>
            ))}
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>

    {dateFilter === DateFilterOption.CustomRange && (
      <div className={styles.customRange}>
        <label className={cx(typography.c3, styles.customRangeLabel)}>
          From
          <input
            className={cx(typography.c3, styles.dateInput)}
            type="date"
            value={customStartDate}
            onChange={(event) => onCustomStartDateChange(event.target.value)}
            onMouseDown={handleDateInputMouseDown}
            onKeyDown={handleDateInputKeyDown}
          />
        </label>
        <label className={cx(typography.c3, styles.customRangeLabel)}>
          To
          <input
            className={cx(typography.c3, styles.dateInput)}
            type="date"
            value={customEndDate}
            onChange={(event) => onCustomEndDateChange(event.target.value)}
            onMouseDown={handleDateInputMouseDown}
            onKeyDown={handleDateInputKeyDown}
          />
        </label>
      </div>
    )}

    <button
      aria-pressed={compareEnabled}
      className={cx(styles.compareTrigger, {
        [styles.compareTriggerActive]: compareEnabled,
      })}
      onClick={() => onCompareToggle(!compareEnabled)}
      type="button"
    >
      <Icon name="rotateCw" className={styles.compareIcon} />
      <span className={cx(typography.c3, styles.compareLabel)}>Compare</span>
      {compareEnabled && (
        <span className={cx(typography.c3, styles.compareActiveValue)}>
          Previous period
        </span>
      )}
      {!compareEnabled && (
        <span className={cx(typography.c3, styles.compareValue)}>Off</span>
      )}
      {compareEnabled && <Icon name="close" className={styles.compareClear} />}
    </button>
  </div>
);
